import { CalendarClock, CarFront, MapPin, TriangleAlert, Users } from 'lucide-react';
import type { DispatchBooking } from '../../api/types';
import { money } from '../../lib/commsFormat';
import { useTimeFormat } from '../../lib/tenantTime';
import { JobLink } from './booking/JobLink';
import { bookingRef, bookingTone, pickupLabel } from './bookingFormat';

/**
 * The rides and quotes of the linked dispatch customer, the affiliate desk this
 * address books for, or both (services/contactLink.js bookerOf).
 *
 * This is the panel's whole reason to exist during a call: the agent is being
 * asked about a specific booking and needs its confirmation number, when it is,
 * where it goes and what state it is in — without leaving the thread for the
 * dispatch dashboard.
 *
 * Times render in the RIDE's zone, not the tenant's: a pickup is a promise made
 * to someone standing at that curb, and showing New York's clock for a Los
 * Angeles pickup is how a chauffeur gets sent three hours late. The zone badge
 * only appears when the two actually differ (foreignZoneAbbr). The palette and
 * the pickup formatter are bookingFormat.ts's, shared with the mail layout.
 */
export function BookingRow({ booking }: { booking: DispatchBooking }) {
  const { zone, foreignZoneAbbr, formats } = useTimeFormat();
  const tone = bookingTone(booking.status.state);
  const rideZone = booking.timeZone ?? zone;
  // Resolved AT the pickup instant, since the abbreviation moves with DST. No
  // pickup date means no time on screen, so there is nothing to badge either.
  const zoneBadge = booking.pickupAt
    ? foreignZoneAbbr(booking.timeZone, new Date(booking.pickupAt))
    : null;

  return (
    <div className="bk-row">
      <div className="bk-row-top">
        {/* The number opens the booking in the dashboard: the one thing an
            agent on a call about it would otherwise retype into a search. */}
        <JobLink kind={booking.kind} id={booking.id} className="bk-ref" icon>
          {bookingRef(booking)}
        </JobLink>
        <span style={{ flex: 1 }} />
        <span className="cv-row-role" style={{ background: tone.bg, color: tone.fg }}>
          {booking.status.label}
        </span>
      </div>

      <div className="bk-when">
        <CalendarClock size={12} />
        <span>{pickupLabel(booking.pickupAt, rideZone, formats)}</span>
        {zoneBadge && <span className="bk-zone">{zoneBadge}</span>}
      </div>

      {(booking.pickup || booking.dropoff) && (
        <div className="bk-route">
          <MapPin size={12} />
          <div className="bk-route-legs">
            <div className="bk-leg" title={booking.pickup ?? ''}>
              {booking.pickup ?? 'Pickup not set'}
            </div>
            <div className="bk-leg bk-leg-to" title={booking.dropoff ?? ''}>
              {booking.dropoff ?? 'Destination not set'}
            </div>
            {booking.stops > 0 && (
              <div className="bk-stops">
                +{booking.stops} stop{booking.stops === 1 ? '' : 's'}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bk-facts">
        {booking.vehicle && (
          <span className="bk-fact" title={booking.vehicle}>
            <CarFront size={11} />
            {booking.vehicle}
          </span>
        )}
        {booking.passengerCount !== null && (
          <span className="bk-fact">
            <Users size={11} />
            {booking.passengerCount}
          </span>
        )}
        {booking.type && <span className="bk-fact">{booking.type}</span>}
        {booking.farmOut && <span className="bk-fact">Farmed out</span>}
        <span style={{ flex: 1 }} />
        {booking.total !== null && <span className="bk-total">{money(booking.total)}</span>}
      </div>
    </div>
  );
}

export function BookingsCard({
  bookings,
  unavailable,
  linked,
  loading,
  firstName,
}: {
  bookings: DispatchBooking[];
  unavailable: string | null;
  linked: boolean;
  loading: boolean;
  firstName: string;
}) {
  return (
    <div className="ctx-card">
      <div className="ctx-card-head">
        <CalendarClock size={14} />
        <span className="comms-section-label">RESERVATIONS</span>
        {bookings.length > 0 && <span className="ctx-card-count">{bookings.length}</span>}
      </div>

      {loading && bookings.length === 0 ? (
        <div className="ctx-empty" aria-busy>
          <span className="sk" style={{ display: 'block', height: 9, width: '80%' }} />
        </div>
      ) : unavailable ? (
        // Naming the cause matters: "no bookings" and "the CRM cannot read the
        // job table" look identical on screen and need opposite responses.
        <div className="ctx-empty ctx-empty-warn">
          <TriangleAlert size={13} />
          <span>{unavailable}</span>
        </div>
      ) : !linked ? (
        <div className="ctx-empty">
          {firstName} isn&rsquo;t linked to a dispatch customer or affiliate yet, so there are no reservations to
          show.
        </div>
      ) : bookings.length === 0 ? (
        <div className="ctx-empty">No rides or quotes booked yet.</div>
      ) : (
        <div className="bk-list">
          {bookings.map((booking) => (
            <BookingRow key={`${booking.kind}-${booking.id}`} booking={booking} />
          ))}
        </div>
      )}
    </div>
  );
}
